import { AlertTriangle, ChevronDown, ChevronRight, Plus, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import type { BoardCell, BoardDay } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { HoursCell, QuantityCell, type AllocationEdit } from './planner-cells';
import { CELL_TONE } from './planner-format';
import type { TeamDaySum, TeamFront, TeamGroup, TeamMemberLine } from './planner-teams';

/**
 * A linha da equipe no planejamento semanal (Fase 30, D10), na grade e nos cartões.
 *
 * Fechada, a equipe é uma linha por frente × serviço com as horas e a meta **somadas** no dia.
 * Aberta, mostra as pessoas, e cada uma continua editável como sempre: mudar as horas ou apagar
 * o dia de uma pessoa é a exceção do dia (D9, RN-425) — a equipe continua na frente com os
 * outros. O dia em que a equipe não está não tem campo: pôr a equipe num dia novo é "Alocar
 * equipe", e uma hora digitada ali nasceria sem a marca, longe da linha onde foi digitada.
 */

export interface TeamBlockProps {
  group: TeamGroup;
  days: readonly BoardDay[];
  editable: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdit: (edit: AllocationEdit) => void;
  onDelete: (edit: AllocationEdit) => void;
  /** Abre a alocação da pessoa — o caminho para tirá-la para outra frente (D9). */
  onAdd: (resourceId: string) => void;
  /** Abre "Alocar equipe" com esta equipe escolhida. */
  onAddTeam?: (teamId: string) => void;
}

const pessoas = (quantas: number): string =>
  `${String(quantas)} ${quantas === 1 ? 'pessoa' : 'pessoas'}`;

function formatQuantity(value: number | undefined, decimals: number): string {
  if (value === undefined) return '—';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** A meta da semana da frente, somados os dias. */
function weekQuantity(front: TeamFront): number | undefined {
  const comMeta = front.days.filter((day) => day.plannedQuantity !== undefined);
  if (comMeta.length === 0) return undefined;
  return comMeta.reduce((soma, day) => soma + (day.plannedQuantity ?? 0), 0);
}

/** As horas somadas da equipe no dia, com quantas pessoas e o aviso de quem passa da jornada. */
export function TeamHoursSum({ sum }: { sum: TeamDaySum }) {
  if (sum.people === 0) {
    return <span className="text-xs text-(--color-content-muted)">—</span>;
  }
  return (
    <span className="flex flex-col items-center">
      <span className={cn('tabular text-sm font-medium', sum.exceeded && CELL_TONE.EXCEEDED)}>
        {Hours.ofQuarters(sum.quarterHours).format()}
        {sum.exceeded && (
          <span className="ml-1 inline-flex items-center">
            <AlertTriangle className="inline size-3" aria-hidden="true" />
            <span className="sr-only">Alguém da equipe passa da jornada</span>
          </span>
        )}
      </span>
      <span className="text-[10px] text-(--color-content-muted)">{pessoas(sum.people)}</span>
    </span>
  );
}

/**
 * O dia que é da equipe, na linha da própria pessoa.
 *
 * A frente × serviço que mistura dias da equipe e dias da pessoa aparece nos dois lugares; no
 * bloco da pessoa, o dia da equipe é só leitura — um campo vazio ali somaria na alocação da
 * equipe (RN-231) sem que a linha da equipe mostrasse de onde veio.
 */
export function TeamHeldMark({ cell, teamName }: { cell: BoardCell; teamName: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-(--color-content-muted)"
      title={`Planejado com a equipe ${teamName}`}
    >
      <Users className="size-3" aria-hidden="true" />
      <span className="tabular">
        {Hours.ofQuarters(cell.allocation?.quarterHours ?? 0).format()}
      </span>
      <span className="sr-only">com a equipe {teamName}</span>
    </span>
  );
}

function memberCellHandlers(
  cell: BoardCell,
  onEdit: (edit: AllocationEdit) => void,
  onDelete: (edit: AllocationEdit) => void,
) {
  const allocation = cell.allocation;
  if (allocation === undefined) return { onCommit: () => undefined };
  return {
    onCommit: (quarterHours: number) => {
      if (quarterHours === 0) {
        onDelete({
          allocationId: allocation.id,
          quarterHours: allocation.quarterHours,
          version: allocation.version,
        });
        return;
      }
      onEdit({ allocationId: allocation.id, quarterHours, version: allocation.version });
    },
    onDelete: () =>
      onDelete({
        allocationId: allocation.id,
        quarterHours: allocation.quarterHours,
        version: allocation.version,
      }),
  };
}

/** Pode mexer nas horas desta frente? A mesma regra da linha da pessoa. */
const frontEditable = (front: TeamFront, editable: boolean): boolean =>
  editable && front.service !== undefined && front.activity.disciplineInactive !== true;

// ── Na grade ─────────────────────────────────────────────────────────────────────

export function TeamGridBlock({
  group,
  days,
  editable,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onAdd,
  onAddTeam,
}: TeamBlockProps) {
  const columnCount = days.length + 2;
  const { team } = group;

  return (
    <>
      <TableRow className="bg-(--color-surface-muted)">
        <TableCell className="sticky left-0 z-10 bg-(--color-surface-muted)">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="flex items-start gap-1.5 rounded-md text-left"
          >
            {expanded ? (
              <ChevronDown className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRight className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            )}
            <span className="flex flex-col">
              <span className="font-medium">{team.name}</span>
              <span className="text-xs text-(--color-content-muted)">
                Equipe · {pessoas(group.people)}
                {!team.isActive && ' · desativada'}
              </span>
            </span>
          </button>
        </TableCell>
        <TableCell colSpan={days.length}>
          {editable && team.isActive && onAddTeam !== undefined && (
            <button
              type="button"
              onClick={() => onAddTeam(team.teamId)}
              className="inline-flex items-center gap-1 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 py-1 text-xs hover:bg-(--color-surface-raised)"
            >
              <Plus className="size-3" aria-hidden="true" />
              Alocar a equipe
            </button>
          )}
        </TableCell>
        <TableCell className="tabular text-sm font-medium">
          {Hours.ofQuarters(group.plannedQuarterHours).format()}
        </TableCell>
      </TableRow>

      {group.fronts.flatMap((front, indice) => {
        const abreFrente =
          indice === 0 ||
          group.fronts[indice - 1]?.activity.activityId !== front.activity.activityId;
        const medida =
          front.service?.unitName === undefined
            ? undefined
            : { nome: front.service.unitName, casas: front.service.unitDecimals ?? 2 };
        const linhaEditavel = frontEditable(front, editable);

        const linhas: ReactNode[] = [];
        if (abreFrente) {
          linhas.push(
            <TableRow key={`${team.teamId}-${front.activity.activityId}-frente`}>
              <TableCell
                colSpan={columnCount}
                className="sticky left-0 z-10 bg-(--color-surface-raised) pl-8 pt-3"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{front.activity.label}</span>
                  <span className="text-xs text-(--color-content-muted)">
                    {front.activity.towerName}
                  </span>
                </div>
              </TableCell>
            </TableRow>,
          );
        }

        linhas.push(
          <TableRow key={`${team.teamId}-${front.key}`}>
            <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-12">
              <div className="flex flex-col">
                <span className="text-sm">{front.service?.name ?? 'Sem serviço'}</span>
                <span className="text-xs text-(--color-content-muted)">
                  horas da equipe, somadas
                </span>
              </div>
            </TableCell>
            {front.days.map((sum) => (
              <TableCell key={sum.date} className="text-center">
                <TeamHoursSum sum={sum} />
              </TableCell>
            ))}
            <TableCell className="tabular text-sm">
              {Hours.ofQuarters(front.plannedQuarterHours).format()}
            </TableCell>
          </TableRow>,
        );

        if (medida !== undefined) {
          linhas.push(
            <TableRow key={`${team.teamId}-${front.key}-meta`}>
              <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-16">
                <span className="text-xs text-(--color-content-muted)">
                  Meta da equipe ({medida.nome})
                </span>
              </TableCell>
              {front.days.map((sum) => (
                <TableCell key={sum.date} className="tabular text-center text-sm">
                  {formatQuantity(sum.plannedQuantity, medida.casas)}
                </TableCell>
              ))}
              <TableCell className="tabular text-xs text-(--color-content-muted)">
                {formatQuantity(weekQuantity(front), medida.casas)}
              </TableCell>
            </TableRow>,
          );
        }

        if (expanded) {
          for (const membro of front.members) {
            linhas.push(
              ...memberGridRows({
                teamId: team.teamId,
                front,
                membro,
                days,
                editable: linhaEditavel,
                canAdd: editable,
                medida,
                onEdit,
                onDelete,
                onAdd,
              }),
            );
          }
        }

        return linhas;
      })}
    </>
  );
}

function memberGridRows({
  teamId,
  front,
  membro,
  days,
  editable,
  canAdd,
  medida,
  onEdit,
  onDelete,
  onAdd,
}: {
  teamId: string;
  front: TeamFront;
  membro: TeamMemberLine;
  days: readonly BoardDay[];
  editable: boolean;
  canAdd: boolean;
  medida: { nome: string; casas: number } | undefined;
  onEdit: (edit: AllocationEdit) => void;
  onDelete: (edit: AllocationEdit) => void;
  onAdd: (resourceId: string) => void;
}): ReactNode[] {
  const { row, activityRow } = membro;
  const chave = `${teamId}-${front.key}-${row.resource.resourceId}`;

  const horas = (
    <TableRow key={chave}>
      <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-16">
        <div className="flex items-center gap-1.5">
          <span className="text-sm">{row.resource.name}</span>
          {canAdd && row.resource.isAllocatable && (
            <button
              type="button"
              onClick={() => onAdd(row.resource.resourceId)}
              aria-label={`Adicionar atividade para ${row.resource.name}`}
              title="Pôr em outra frente"
              className="rounded-md p-0.5 text-(--color-content-muted) hover:bg-(--color-surface-muted)"
            >
              <Plus className="size-3" aria-hidden="true" />
            </button>
          )}
        </div>
      </TableCell>
      {activityRow.cells.map((cell, index) => {
        const day = days[index]!;
        return (
          <TableCell key={cell.date} className="text-center">
            <HoursCell
              quarterHours={cell.allocation?.quarterHours ?? 0}
              dayTotal={row.dayTotals[index]!}
              isWorkday={day.isWorkday}
              exceedsCapacity={cell.allocation?.exceedsCapacity ?? false}
              justification={cell.allocation?.justification}
              resourceName={row.resource.name}
              activityLabel={front.activity.label}
              dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
              editable={editable && cell.allocation !== undefined}
              {...memberCellHandlers(cell, onEdit, onDelete)}
            />
          </TableCell>
        );
      })}
      <TableCell className="tabular text-sm text-(--color-content-muted)">
        {Hours.ofQuarters(activityRow.plannedQuarterHours).format()}
      </TableCell>
    </TableRow>
  );

  if (medida === undefined) return [horas];

  const meta = (
    <TableRow key={`${chave}-meta`}>
      <TableCell className="sticky left-0 z-10 bg-(--color-surface-raised) pl-20">
        <span className="text-xs text-(--color-content-muted)">Meta ({medida.nome})</span>
      </TableCell>
      {activityRow.cells.map((cell, index) => {
        const day = days[index]!;
        const allocation = cell.allocation;
        return (
          <TableCell key={`${cell.date}-meta`} className="text-center">
            <QuantityCell
              plannedQuantity={allocation?.plannedQuantity}
              executedQuantity={allocation?.executedQuantity}
              unitName={medida.nome}
              decimals={medida.casas}
              resourceName={row.resource.name}
              activityLabel={front.activity.label}
              dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
              editable={editable && allocation !== undefined}
              onCommit={(plannedQuantity) => {
                if (allocation === undefined) return;
                onEdit({
                  allocationId: allocation.id,
                  quarterHours: allocation.quarterHours,
                  version: allocation.version,
                  plannedQuantity,
                });
              }}
            />
          </TableCell>
        );
      })}
      <TableCell />
    </TableRow>
  );

  return [horas, meta];
}

// ── Nos cartões (celular) ─────────────────────────────────────────────────────────

export function TeamCard({
  group,
  days,
  editable,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onAdd,
  onAddTeam,
}: TeamBlockProps) {
  const { team } = group;

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
      <header className="flex items-start justify-between gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex min-h-11 min-w-0 items-start gap-1.5 text-left"
        >
          {expanded ? (
            <ChevronDown className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium">{team.name}</span>
            <span className="text-xs text-(--color-content-muted)">
              Equipe · {pessoas(group.people)}
              {!team.isActive && ' · desativada'}
            </span>
          </span>
        </button>
        <span className="tabular shrink-0 text-sm font-medium">
          {Hours.ofQuarters(group.plannedQuarterHours).format()}
        </span>
      </header>

      {group.fronts.map((front) => {
        const medida =
          front.service?.unitName === undefined
            ? undefined
            : { nome: front.service.unitName, casas: front.service.unitDecimals ?? 2 };
        const linhaEditavel = frontEditable(front, editable);
        return (
          <div
            key={front.key}
            className="flex min-w-0 flex-col gap-2 border-t border-(--color-border-subtle) pt-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{front.activity.label}</p>
                <p className="truncate text-xs text-(--color-content-muted)">
                  {front.service?.name ?? 'Sem serviço'}
                </p>
              </div>
              <span className="tabular shrink-0 text-sm">
                {Hours.ofQuarters(front.plannedQuarterHours).format()}
              </span>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {front.days.map((sum, index) => (
                <div key={sum.date} className="flex flex-col items-center gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                    {days[index]!.weekdayLabel}
                  </span>
                  <TeamHoursSum sum={sum} />
                </div>
              ))}
            </div>

            {medida !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                Meta da equipe em {medida.nome} ·{' '}
                {formatQuantity(weekQuantity(front), medida.casas)} na semana
              </p>
            )}

            {expanded &&
              front.members.map(({ row, activityRow }) => (
                <div
                  key={row.resource.resourceId}
                  className="flex min-w-0 flex-col gap-1.5 rounded-md bg-(--color-surface) p-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm">{row.resource.name}</p>
                    {editable && row.resource.isAllocatable && (
                      <button
                        type="button"
                        onClick={() => onAdd(row.resource.resourceId)}
                        aria-label={`Adicionar atividade para ${row.resource.name}`}
                        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md px-2 text-xs text-(--color-content-muted)"
                      >
                        <Plus className="size-3" aria-hidden="true" />
                        Outra frente
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {activityRow.cells.map((cell, index) => {
                      const day = days[index]!;
                      return (
                        <label key={cell.date} className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-(--color-content-muted)">
                            {day.weekdayLabel}
                          </span>
                          <HoursCell
                            quarterHours={cell.allocation?.quarterHours ?? 0}
                            dayTotal={row.dayTotals[index]!}
                            isWorkday={day.isWorkday}
                            exceedsCapacity={cell.allocation?.exceedsCapacity ?? false}
                            justification={cell.allocation?.justification}
                            resourceName={row.resource.name}
                            activityLabel={front.activity.label}
                            dayLabel={`${day.weekdayLabel} ${day.shortLabel}`}
                            editable={linhaEditavel && cell.allocation !== undefined}
                            {...memberCellHandlers(cell, onEdit, onDelete)}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        );
      })}

      {editable && team.isActive && onAddTeam !== undefined && (
        <button
          type="button"
          onClick={() => onAddTeam(team.teamId)}
          className="inline-flex min-h-11 items-center justify-center gap-1 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 text-sm"
        >
          <Plus className="size-4" aria-hidden="true" />
          Alocar a equipe
        </button>
      )}
    </section>
  );
}
